import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useEnquirySources } from "@/modules/enquiries/hooks/useEnquiries";
import { useUpdateApiKey } from "../hooks/useApiKeys";
import type { ApiKeyItem } from "../types";

type Props = {
  apiKey: ApiKeyItem | null;
  onClose: () => void;
};

/** Edits everything except the key value itself (which is never recoverable). */
export function EditApiKeyDrawer({ apiKey, onClose }: Props) {
  const update = useUpdateApiKey(apiKey?.id ?? "");
  const { data: sources = [] } = useEnquirySources();

  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [origins, setOrigins] = useState("");
  const [rateLimit, setRateLimit] = useState(100);
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (apiKey) {
      setName(apiKey.name);
      setSource(apiKey.source);
      setOrigins(apiKey.allowedOrigins.join("\n"));
      setRateLimit(apiKey.rateLimit);
      setIsActive(apiKey.isActive);
      setError(null);
    }
  }, [apiKey]);

  if (!apiKey) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({
        name: name.trim(),
        source,
        allowedOrigins: origins
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean),
        rateLimit,
        isActive,
      });
      onClose();
    } catch (err) {
      setError((err as Error)?.message ?? "Update failed");
    }
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <h2 className="text-lg font-semibold text-foreground">
            Edit API Key
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex flex-1 flex-col overflow-y-auto"
        >
          <div className="flex-1 space-y-5 p-6">
            <div
              className="rounded-md border bg-[hsl(210,30%,97%)] p-3 text-xs text-muted-foreground"
              style={{ borderColor: "hsl(210,30%,85%)" }}
            >
              Key:{" "}
              <span className="font-mono text-foreground">
                {apiKey.keyPrefix}…
              </span>{" "}
              · the full key cannot be shown again.
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Source</Label>
              <Select
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {sources.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Allowed Origins</Label>
              <textarea
                value={origins}
                onChange={(e) => setOrigins(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,100%,50%)]"
                style={{ borderColor: "hsl(210,30%,85%)" }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Rate Limit (req/hour)</Label>
                <Input
                  type="number"
                  min={1}
                  value={rateLimit}
                  onChange={(e) => setRateLimit(Number(e.target.value))}
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                  Active
                </label>
              </div>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Save
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
